import { useEffect, useRef } from 'react';

interface TableRow {
  id: string;
  date: string;
  pricingType?: string;
  service: string;
  time: string;
  price: string;
  discount: { value: number; type: 'percentage' | 'fixed' };
  subtotal: number;
  customWorkingDescription?: string;
  [key: string]: any;
}

interface UseTableRowUpdatesProps {
  hoursPerDayType: string;
  hourlyPrice: string;
  serviceDescription: string;
  deductionType: string;
  tableRowsByFreelancer: Record<string, TableRow[]>;
  setTableRowsByFreelancer: React.Dispatch<React.SetStateAction<Record<string, TableRow[]>>>;
  convertToDecimalHours: (value: string) => number;
  convertToTimeFormat: (decimalHours: number) => string;
}

export const useTableRowUpdates = ({
  hoursPerDayType,
  hourlyPrice,
  serviceDescription,
  deductionType,
  tableRowsByFreelancer,
  setTableRowsByFreelancer,
  convertToDecimalHours,
  convertToTimeFormat
}: UseTableRowUpdatesProps) => {
  
  // Store previous serviceDescription value to track changes
  const prevServiceDescriptionRef = useRef<string>('');

  // Update existing table rows when hoursPerDayType changes
  useEffect(() => {
    // Don't update table rows if hoursPerDayType is empty, '0', or '00:00'
    if (!hoursPerDayType || hoursPerDayType === '0' || hoursPerDayType === '00:00' || hoursPerDayType === '0.00') {
       return;
    }
    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const updatedRows: typeof prevRowsByFreelancer = {};
      let hasChanges = false;

      Object.keys(prevRowsByFreelancer).forEach(freelancerId => {
        const rows = prevRowsByFreelancer[freelancerId] || [];
        
        const updatedFreelancerRows = rows.map(row => {
          // Only update if pricing type is 'Tim' (hourly)
          if (row.pricingType === 'Tim' || !row.pricingType) {
            // Convert to decimal hours first (handles both formats)
            const decimalHours = convertToDecimalHours(hoursPerDayType);
            // Then convert to hh:mm format for table display
            const newTimeValue = convertToTimeFormat(decimalHours);
            
            if (row.time !== newTimeValue) {
              hasChanges = true;
              return { ...row, time: newTimeValue };
            }
          }
          return row;
        });

        updatedRows[freelancerId] = updatedFreelancerRows;
      });

      if (hasChanges) {
        return updatedRows;
      }
      return prevRowsByFreelancer;
    });
  }, [hoursPerDayType, convertToDecimalHours, convertToTimeFormat, setTableRowsByFreelancer]);

  // Update existing table rows when hourlyPrice changes
  useEffect(() => {
    if (!hourlyPrice) return;
    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const updatedRows: typeof prevRowsByFreelancer = {};
      let hasChanges = false;

      Object.keys(prevRowsByFreelancer).forEach(freelancerId => {
        const rows = prevRowsByFreelancer[freelancerId] || [];
        
        const updatedFreelancerRows = rows.map(row => {
          const newPriceValue = hourlyPrice;
          if (row.price !== newPriceValue) {
            hasChanges = true;
            return { ...row, price: newPriceValue };
          }
          return row;
        });

        updatedRows[freelancerId] = updatedFreelancerRows;
      });

      if (hasChanges) {
        return updatedRows;
      }
      return prevRowsByFreelancer;
    });
  }, [hourlyPrice, setTableRowsByFreelancer]);

  // Update existing table rows when serviceDescription changes (only for non-RUT/ROT)
  useEffect(() => {
    // Only update if deduction type is not RUT/ROT
    const isRutOrRotDeduction = deductionType === 'rut' || deductionType === 'rot';
    if (isRutOrRotDeduction) return;
    
    const prevServiceDescription = prevServiceDescriptionRef.current;
    
    setTableRowsByFreelancer(prevRowsByFreelancer => {
      const updatedRows: typeof prevRowsByFreelancer = {};
      let hasChanges = false;

      Object.keys(prevRowsByFreelancer).forEach(freelancerId => {
        const rows = prevRowsByFreelancer[freelancerId] || [];
        
        const updatedFreelancerRows = rows.map(row => {
          const newServiceValue = serviceDescription;
          if (!row.customWorkingDescription && newServiceValue) {
            hasChanges = true;
            return { ...row, customWorkingDescription: newServiceValue };
          } else if (row.customWorkingDescription === prevServiceDescription && newServiceValue) {
            hasChanges = true;
            return { ...row, customWorkingDescription: newServiceValue };
          }
          return row;
        });

        updatedRows[freelancerId] = updatedFreelancerRows;
      });

      if (hasChanges) {
        prevServiceDescriptionRef.current = serviceDescription;
        return updatedRows;
      }
      return prevRowsByFreelancer;
    });
  }, [serviceDescription, deductionType, setTableRowsByFreelancer]);
};
